import { useEffect, useRef } from 'react';
import { isWeb } from 'shared/src/utils';
import type { PostTrackingLinksAccessLogsRequest } from 'shared/src/api';
import { usePostTrackingLinksAccessLogs } from '../queries/usePostTrackingLinksAccessLogs';
import {
  useGoodsRoute,
  type GoodsDetailExtras,
} from '../../../hooks/useGoodsRoute';

/**
 * 트래킹 링크(utm/ad/tracking) 진입 시 /api/v2/tracking-links/access-logs/ 호출.
 *
 * native(iOS TrackingLinkService / Android DeeplinkAnalytics)와 동일하게
 * UTM_SOURCE가 존재할 때만 호출. 페이로드 키는 native와 정합 (UTM_*, AD_*,
 * TRACKING_*, RAW_LINK, PATH, PARAMETERS, AIRBRIDGE_REFERRER).
 *
 * - web: window.location.search에서 추출
 * - 앱: params.extras.airbridge_referrer 파싱 + tracking_content 직접 사용
 */
export const useTrackingLinkAccessLog = () => {
  const { mutate } = usePostTrackingLinksAccessLogs();
  const { params } = useGoodsRoute<'GoodsDetail'>();
  const isSent = useRef(false);

  const extras = params.extras;

  useEffect(() => {
    if (isSent.current) return;

    const payload = isWeb
      ? buildPayloadFromWebUrl()
      : buildPayloadFromExtras(extras);

    if (payload === null) return;

    isSent.current = true;
    mutate(payload);

    if (isWeb) {
      cleanTrackingParamsFromUrl();
    }
  }, [extras, mutate]);
};

const TRACKING_QUERY_KEYS = [
  'utm_source',
  'utm_content',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'ad_group',
  'ad_creative',
  'tracking_source',
  'tracking_term',
  'tracking_content',
] as const;

const buildPayloadFromWebUrl =
  (): PostTrackingLinksAccessLogsRequest | null => {
    const search = new URLSearchParams(window.location.search);
    const utmSource = search.get('utm_source');
    if (!utmSource) return null;

    const parameters: Record<string, unknown> = {};
    search.forEach((value, key) => {
      parameters[key.toUpperCase()] = value;
    });

    const extracted = TRACKING_QUERY_KEYS.reduce<Record<string, string>>(
      (acc, key) => {
        const value = search.get(key);
        if (value != null) acc[key.toUpperCase()] = value;
        return acc;
      },
      {},
    );

    return {
      UTM_SOURCE: utmSource,
      ...extracted,
      RAW_LINK: window.location.href,
      PATH: `${window.location.origin}${window.location.pathname}`,
      PARAMETERS: parameters,
    };
  };

const cleanTrackingParamsFromUrl = () => {
  const search = new URLSearchParams(window.location.search);
  TRACKING_QUERY_KEYS.forEach(key => search.delete(key));
  const nextSearch = search.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
  window.history.replaceState(null, '', nextUrl);
};

const buildPayloadFromExtras = (
  extras: GoodsDetailExtras | undefined,
): PostTrackingLinksAccessLogsRequest | null => {
  const referrer = extras?.airbridge_referrer;
  if (!referrer) return null;

  const referrerParams = new URLSearchParams(referrer);
  const utmSource =
    referrerParams.get('channel') ?? referrerParams.get('utm_source');
  if (!utmSource) return null;

  const referrerMap: Record<string, unknown> = {};
  referrerParams.forEach((value, key) => {
    referrerMap[key.toUpperCase()] = value;
  });

  return {
    UTM_SOURCE: utmSource,
    UTM_CONTENT:
      referrerParams.get('content') ??
      referrerParams.get('utm_content') ??
      undefined,
    UTM_MEDIUM:
      referrerParams.get('medium') ??
      referrerParams.get('utm_medium') ??
      undefined,
    UTM_CAMPAIGN:
      referrerParams.get('campaign') ??
      referrerParams.get('utm_campaign') ??
      undefined,
    UTM_TERM:
      referrerParams.get('term') ??
      referrerParams.get('utm_term') ??
      undefined,
    TRACKING_CONTENT: extras?.tracking_content ?? undefined,
    AIRBRIDGE_REFERRER: referrerMap,
  };
};
